import React, { useState, useEffect, useRef } from 'react';
import { getChannelMessages, publishChannelMessage, ChannelMessage } from '../lib/communities';
import { recordSignal } from '../lib/signals';
import { asText, pubkeyShortHash } from '../lib/sanitize';
import { seedFile } from '../lib/torrent';
import { saveFile } from '../lib/fileStorage';
import { MediaAttachment } from './MediaAttachment';
import { getLikeCount, hasLiked, saveIncomingLike, addLikesListener } from '../lib/likes';
import { loadOrCreateIdentity } from '../lib/identity';
import { publishEvent } from '../lib/sync';
import { COLORS, SPACING } from '../styles/theme';

async function compressImage(file: File, maxBytes = 200 * 1024): Promise<Blob> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const maxDim = 1280;
      let w = img.naturalWidth, h = img.naturalHeight;
      if (w > maxDim) { h = Math.round(h * maxDim / w); w = maxDim; }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      let quality = 0.85;
      const tryCompress = () => {
        canvas.toBlob((blob) => {
          if (!blob) { resolve(new Blob([file])); return; }
          if (blob.size <= maxBytes || quality <= 0.1) { resolve(blob); return; }
          quality -= 0.15;
          tryCompress();
        }, 'image/jpeg', quality);
      };
      tryCompress();
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(new Blob([file])); };
    img.src = url;
  });
}


interface ChannelViewProps {
  communityId: string;
  channelId: string;
  channelName: string;
  onBack: () => void;
}

export function ChannelView({ communityId, channelId, channelName, onBack }: ChannelViewProps) {
  const [messages, setMessages] = useState<ChannelMessage[]>(() => getChannelMessages(communityId, channelId));
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [likeRevision, setLikeRevision] = useState(0);
  const bottomRef = useRef<HTMLDivElement>(null);
  const identity = useRef(loadOrCreateIdentity()).current;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => addLikesListener(() => setLikeRevision(r => r + 1)), []);

  async function handleLike(msgId: string) {
    if (hasLiked(msgId, identity.userId)) return;
    const event = await publishEvent('like', { targetId: msgId, targetType: 'channel_message', userId: identity.userId });
    saveIncomingLike({ id: event.id, targetId: msgId, targetType: 'channel_message', userId: identity.userId, createdAt: event.timestamp });
  }

  async function handleSend() {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSending(true);
    await publishChannelMessage(communityId, channelId, trimmed, replyTo || undefined);
    setText('');
    setReplyTo(null);
    setMessages(getChannelMessages(communityId, channelId));
    setSending(false);
  }

  async function handleAttach(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSending(true);
    const isImage = file.type.startsWith('image/');
    let seedBlob: Blob = file;
    let attachmentType: 'image' | 'video' | undefined;
    if (isImage) {
      if (file.size > 2 * 1024 * 1024) { setSending(false); return; }
      seedBlob = await compressImage(file);
      attachmentType = 'image';
    } else if (file.type.startsWith('video/')) {
      attachmentType = 'video';
    }
    const seedFile_ = new File([seedBlob], file.name, { type: isImage ? 'image/jpeg' : file.type });
    const { infoHash, magnetURI } = await seedFile(seedFile_);
    await saveFile(infoHash, seedBlob, file.name);
    await recordSignal('channel-media-send', 'share', magnetURI);
    await publishChannelMessage(communityId, channelId, text.trim() || file.name, replyTo || undefined, magnetURI, attachmentType);
    setText('');
    setReplyTo(null);
    setMessages(getChannelMessages(communityId, channelId));
    setSending(false);
  }

  const replyingTo = replyTo ? messages.find(m => m.id === replyTo) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, padding: `${SPACING.sm} ${SPACING.md}`, borderBottom: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontSize: '12px', fontFamily: 'monospace' }}>←</button>
        <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: COLORS.text }}>#{channelName}</span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: SPACING.md, display: 'flex', flexDirection: 'column', gap: SPACING.md }}>
        {messages.map(msg => (
          <div key={msg.id}>
            {msg.replyToId && (() => {
              const parent = messages.find(m => m.id === msg.replyToId);
              return parent ? (
                <div style={{ fontFamily: 'monospace', fontSize: '10px', color: COLORS.textMuted, paddingLeft: SPACING.md, borderLeft: `2px solid ${COLORS.border}`, marginBottom: 2 }}>
                  ↩ {asText(parent.content).slice(0, 60)}
                </div>
              ) : null;
            })()}
            <div style={{ display: 'flex', gap: SPACING.sm, alignItems: 'baseline', marginBottom: 2 }}>
              <span style={{ fontFamily: 'monospace', fontSize: '11px', color: COLORS.accent }}>
                {asText(msg.userId)} [{pubkeyShortHash(msg.pubkey)}]
              </span>
              <span style={{ fontFamily: 'monospace', fontSize: '10px', color: COLORS.textMuted }}>
                {new Date(msg.timestamp).toLocaleTimeString()}
              </span>
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: '13px', color: COLORS.text, marginBottom: 2 }}>
              {asText(msg.content)}
            </div>
            {msg.magnetURI && <MediaAttachment magnetURI={msg.magnetURI} attachmentType={msg.attachmentType} thumbnailDataUrl={msg.thumbnailDataUrl} />}
            <div style={{ display: 'flex', gap: SPACING.md, marginTop: 2 }}>
              <button
                onClick={() => setReplyTo(msg.id)}
                style={{ background: 'transparent', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontSize: '10px', fontFamily: 'monospace', padding: 0 }}
              >
                ↩ reply
              </button>
              {(() => {
                const count = getLikeCount(msg.id); // eslint-disable-line react-hooks/exhaustive-deps
                const liked = hasLiked(msg.id, identity.userId);
                void likeRevision; // causes re-render when likes change
                return (
                  <button
                    onClick={() => handleLike(msg.id)}
                    disabled={liked}
                    style={{ background: 'transparent', border: 'none', color: liked ? COLORS.accent : COLORS.textMuted, cursor: liked ? 'default' : 'pointer', fontSize: '10px', fontFamily: 'monospace', padding: 0 }}
                  >
                    ♥{count > 0 ? ` ${count}` : ''}
                  </button>
                );
              })()}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {replyingTo && (
        <div style={{ padding: `${SPACING.xs} ${SPACING.md}`, background: COLORS.border, fontFamily: 'monospace', fontSize: '11px', color: COLORS.textMuted, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span>Replying to: {asText(replyingTo.content).slice(0, 60)}</span>
          <button onClick={() => setReplyTo(null)} style={{ background: 'transparent', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontSize: '16px' }}>×</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: SPACING.sm, padding: SPACING.md, borderTop: `1px solid ${COLORS.border}`, alignItems: 'center', flexShrink: 0 }}>
        <input
          type="text"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder={`Message #${channelName}`}
          disabled={sending}
          style={{ flex: 1, fontFamily: 'monospace', fontSize: '13px', background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`, padding: SPACING.xs }}
        />
        <label style={{ cursor: 'pointer', color: COLORS.textMuted, fontSize: '16px' }} title="Attach file">
          📎
          <input type="file" onChange={handleAttach} style={{ display: 'none' }} disabled={sending} />
        </label>
        <button
          onClick={handleSend}
          disabled={sending || !text.trim()}
          style={{ background: COLORS.accent, color: COLORS.bg, border: 'none', padding: `${SPACING.xs} ${SPACING.sm}`, cursor: 'pointer', fontFamily: 'monospace', fontSize: '13px', fontWeight: 'bold' }}
        >
          Send
        </button>
      </div>
    </div>
  );
}
